import { useState, useEffect, useRef } from "react";

// ─── IMAGES ───────────────────────────────────────────────────────────────────
import img1 from "./Images/success1.png";
import img2 from "./Images/success2.png";
import img3 from "./Images/success3.png";
import img4 from "./Images/success4.png";

// ─── DATA (1 image per alumni) ────────────────────────────────────────────────

const ALUMNI = [
  {
    name: "Harshitha T",
    course: "NTT 2024–25",
    location: "Hassan",
    quote: "Anabel NTT is the perfect place for home-based learning. The staff is very cooperative and understanding.",
    image: img1,
  },
  {
    name: "Priyanka",
    course: "NTT 2024–25",
    location: "Vijayapura",
    quote: "Anabel is a genuine and trusted institute. I received my certificate without any issues.",
    image: img2,
  },
  {
    name: "Shubha K J",
    course: "NTT 2024–25",
    location: "Madanapura",
    quote: "The course helped me grow personally and professionally. I am grateful. Now working at Indian English School, Hanbal.",
    image: img3,
  },
  {
    name: "Manasa S K",
    course: "NTT 2024–25",
    location: "Hassan",
    quote: "I had a smooth learning experience with Anabel. Everything was well organized.",
    image: img4,
  },
];

// ─── SINGLE ALUMNI CARD ───────────────────────────────────────────────────────

// SEO Upgrade: Added 'ariaHidden' to prevent screen readers from reading duplicated carousel items twice
function AlumniCard({ alumni, ariaHidden }) {
  return (
    // SEO Upgrade: Changed generic <div> to semantic <article>
    <article 
      aria-hidden={ariaHidden}
      className="flex-shrink-0 w-80 bg-white rounded-2xl overflow-hidden border border-gray-200 shadow-sm hover:shadow-xl hover:border-[#1800ad] transition-all duration-300 flex flex-col"
    >
      {/* Photo */}
      <div className="relative overflow-hidden" style={{ height: 240 }}>
        <img
          src={alumni.image}
          // SEO Upgrade: More descriptive alt text for image search
          alt={`Portrait of ${alumni.name}, AnaBel Institute Alumnus`}
          // SEO Upgrade: Lazy load images below the fold to improve page speed scores
          loading="lazy" 
          className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
        />
        {/* tri-color top bar */}
        <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-[#d11210] via-[#ffde59] to-[#1800ad]" />
        {/* fade to white */}
        <div className="absolute bottom-0 left-0 right-0 h-14 bg-gradient-to-t from-white to-transparent" />
        {/* course badge */}
        <div className="absolute top-3 left-3">
          <span className="text-xs font-extrabold uppercase tracking-wider bg-[#ffde59] text-[#1800ad] px-3 py-1.5 rounded-full shadow">
            {alumni.course}
          </span>
        </div>
      </div>

      {/* Content */}
      {/* SEO Upgrade: Changed generic <div> to semantic <blockquote> */}
      <blockquote className="flex flex-col flex-1 px-6 pb-6 pt-2 gap-3 m-0">
        <div className="text-4xl leading-none text-[#1800ad]" aria-hidden="true">"</div>
        <p className="text-base text-gray-600 leading-relaxed italic flex-1">{alumni.quote}</p>
        <div className="h-px bg-gradient-to-r from-[#ffde59] to-transparent mt-2" />
        
        {/* SEO Upgrade: Changed to semantic <footer> and <cite> for blockquotes */}
        <footer className="flex items-center justify-between pt-2">
          <div>
            <cite className="text-base font-extrabold text-gray-900 not-italic block">{alumni.name}</cite>
            <p className="text-sm text-[#d11210] font-medium mt-0.5">📍 {alumni.location}</p>
          </div>
          <div className="w-10 h-10 rounded-full bg-[#1800ad] flex items-center justify-center text-white text-base font-black shadow" aria-hidden="true">
            {alumni.name.charAt(0)}
          </div>
        </footer>
      </blockquote>
    </article>
  );
}

// ─── MAIN ─────────────────────────────────────────────────────────────────────

export default function Aluminisuccessstories() {
  const [paused, setPaused] = useState(false);
  const trackRef = useRef(null);
  const posRef = useRef(0);
  const rafRef = useRef(null);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const SPEED = 0.6;
    const animate = () => {
      if (!paused) {
        posRef.current += SPEED;
        const half = track.scrollWidth / 2;
        if (posRef.current >= half) posRef.current -= half;
        track.style.transform = `translateX(-${posRef.current}px)`;
      }
      rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, [paused]);

  const doubled = [...ALUMNI, ...ALUMNI];

  // SEO Upgrade: Generate JSON-LD Schema for Google to read the reviews
  const reviewSchema = {
    "@context": "https://schema.org",
    "@type": "EducationalOrganization",
    "name": "AnaBel Institute",
    "review": ALUMNI.map(alumni => ({
      "@type": "Review",
      "author": {
        "@type": "Person",
        "name": alumni.name
      },
      "reviewBody": alumni.quote,
      "reviewRating": {
        "@type": "Rating",
        "ratingValue": "5",
        "bestRating": "5"
      }
    }))
  };

  return (
    // SEO Upgrade: Changed <div w-full...> to <section>
    <section aria-labelledby="alumni-heading" className="w-full bg-[#f8f9fc] py-16 overflow-hidden">
      
      {/* JSON-LD Script Injection */}
      <script 
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(reviewSchema).replace(/</g, '\\u003c') }}
      />

      {/* Header */}
      <header className="text-center mb-12 px-4">
        <span className="inline-block text-xs font-extrabold tracking-[0.22em] uppercase bg-[#e8e6fb] border border-[#1800ad] text-[#1800ad] px-4 py-1.5 rounded-full mb-4">
          ★ Alumni Success Stories
        </span>
        {/* SEO Upgrade: Added ID to link to the section's aria-labelledby */}
        <h2 id="alumni-heading" className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-3">
          Real Students. <span className="text-[#d11210]">Real Results.</span>
        </h2>
        <p className="text-base text-gray-500">Our graduates speak for themselves.</p>
      </header>

      {/* Carousel */}
      <div
        className="relative"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onTouchStart={() => setPaused(true)}
        onTouchEnd={() => setPaused(false)}
        role="region"
        aria-label="Student Testimonials Carousel"
      >
        <div className="absolute left-0 top-0 bottom-0 w-20 z-10 pointer-events-none"
          style={{ background: "linear-gradient(to right, #f8f9fc, transparent)" }} />
        <div className="absolute right-0 top-0 bottom-0 w-20 z-10 pointer-events-none"
          style={{ background: "linear-gradient(to left, #f8f9fc, transparent)" }} />

        <div className="flex overflow-visible">
          <div ref={trackRef} className="flex gap-6 will-change-transform" style={{ paddingLeft: 40, paddingRight: 40 }}>
            {/* SEO Upgrade: Pass ariaHidden true to the duplicated items so screen readers don't read them twice */}
            {doubled.map((a, i) => (
              <AlumniCard key={i} alumni={a} ariaHidden={i >= ALUMNI.length ? "true" : "false"} />
            ))}
          </div>
        </div>
      </div>

      <p className="text-center text-xs text-gray-400 mt-5 tracking-wide" aria-hidden="true">
        Hover to pause · Auto-scrolling
      </p>

      {/* Admissions Banner */}
      {/* SEO Upgrade: Changed to semantic <aside> to denote a call-to-action block separate from the testimonials */}
      <aside aria-label="Admissions Call to Action" className="mt-14 mx-4 sm:mx-10 bg-[#1800ad] rounded-2xl overflow-hidden shadow-lg shadow-[#1800ad]/20">
        <div className="relative px-6 sm:px-12 py-8 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="absolute inset-0 opacity-[0.08]"
            style={{ backgroundImage: "repeating-linear-gradient(45deg,#fff 0,#fff 1px,transparent 0,transparent 50%)", backgroundSize: "12px 12px" }} />
          <div className="relative text-center sm:text-left">
            <p className="text-xs font-bold uppercase tracking-widest text-[#ffde59] mb-2">
              Admissions Open · ಪ್ರವೇಶ ತೆರೆದಿದೆ
            </p>
            <h3 className="text-white font-extrabold text-2xl">Don't Miss This Batch — Apply Now</h3>
            <p className="text-white/70 text-sm mt-1.5">Limited seats available · ಸೀಮಿತ ಆಸನಗಳು ಲಭ್ಯ</p>
          </div>
          <div className="relative flex flex-col sm:flex-row items-center gap-6 shrink-0">
            <div className="text-center sm:text-right">
              <p className="text-xs text-white/70 font-bold uppercase tracking-wider mb-1">Call Now</p>
              <p className="text-[#ffde59] font-extrabold text-xl leading-tight">9964451828</p>
              {/* <p className="text-[#ffde59] font-extrabold text-xl leading-tight">8884618115</p> */}
            </div>
            <button className="bg-[#d11210] hover:bg-[#a30e0c] text-white font-extrabold text-base px-8 py-4 rounded-xl transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5">
              Apply Now →
            </button>
          </div>
        </div>
      </aside>
    </section>
  );
}